import { useState } from "react";

import { shortLabel } from "@/lib/format";
import { STATUS_LIST } from "@/lib/domain-constants";
import { MemberAvatar } from "@/components/team/AssigneeSelect";
import { shortDate } from "@/components/team/AttentionPanel";
import type { TeamActivityEntry } from "@/types/domain";

/** Redacta el evento en una frase, con el nombre de la licitación en negrita. */
function sentence(entry: TeamActivityEntry, isMine: boolean) {
  const actor = isMine ? "Tú" : (entry.actor?.name ?? "Alguien");
  const target = entry.contracting.label;

  switch (entry.type) {
    case "STATUS_CHANGED": {
      const to =
        entry.toStatus === null
          ? null
          : (STATUS_LIST.find((s) => s.value === entry.toStatus)?.label ??
            null);
      return {
        actor,
        verb: to ? (isMine ? "moviste" : "movió") : isMine ? "quitaste" : "quitó",
        target,
        tail: to ? ` a ${to}` : " del pipeline",
      };
    }
    case "ASSIGNED": {
      // Tomarla uno mismo no es un traspaso: decirlo así evita el "Ana traspasó X
      // a Ana", que se lee como un error del sistema.
      const toSelf = entry.target && entry.target.id === entry.actor?.id;
      return {
        actor,
        verb: isMine ? "traspasaste" : "traspasó",
        target,
        tail: !entry.target
          ? " y la dejó sin dueño"
          : toSelf
            ? isMine
              ? " a ti mismo"
              : " a sí mismo"
            : ` a ${entry.target.name}`,
      };
    }
    case "NOTE_UPDATED":
    case "NOTE_ADDED":
      return {
        actor,
        verb: isMine ? "comentaste en" : "comentó en",
        target,
        tail: "",
      };
    case "TASK_CREATED":
      return {
        actor,
        verb: isMine ? "dejaste una tarea en" : "dejó una tarea en",
        target,
        tail: entry.target ? ` para ${entry.target.name}` : "",
      };
    case "TASK_COMPLETED":
      return {
        actor,
        verb: isMine ? "completaste una tarea de" : "completó una tarea de",
        target,
        tail: "",
      };
    default:
      return { actor, verb: "abrió", target, tail: "" };
  }
}

const COLLAPSED_ENTRIES = 6;

/**
 * Los últimos movimientos, redactados. Es lo que convierte el tablero en algo
 * vivo: se ve quién hizo qué sin abrir cada licitación.
 */
export function ActivityFeed({
  title,
  entries,
  currentUserId,
  footnote,
  onOpen,
}: {
  title: string;
  entries: TeamActivityEntry[];
  currentUserId?: string;
  footnote?: React.ReactNode;
  onOpen: (contractingId: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? entries : entries.slice(0, COLLAPSED_ENTRIES);

  return (
    <section className="flex flex-col gap-3 rounded-2xl border bg-card p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-base font-bold">{title}</h3>
        {entries.length > COLLAPSED_ENTRIES && (
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            className="shrink-0 cursor-pointer text-xs font-semibold text-primary hover:underline"
          >
            {expanded ? "Ver menos" : "Ver todo"}
          </button>
        )}
      </div>

      {entries.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">
          Todavía no hay movimientos registrados.
        </p>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {visible.map((entry) => {
            const isMine = entry.actor?.id === currentUserId;
            const text = sentence(entry, isMine);
            return (
              <li key={entry.id} className="flex items-start gap-3">
                {entry.actor ? (
                  <MemberAvatar
                    member={{
                      id: entry.actor.id,
                      name: entry.actor.name,
                      image: entry.actor.image,
                    }}
                    className="size-7 shrink-0"
                  />
                ) : (
                  <span className="size-7 shrink-0 rounded-full bg-slate-100" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] leading-snug text-slate-600 dark:text-slate-300">
                    <span className="font-semibold text-slate-800 dark:text-slate-100">
                      {text.actor}
                    </span>{" "}
                    {text.verb}{" "}
                    <button
                      type="button"
                      title={text.target}
                      onClick={() => onOpen(entry.contracting.id)}
                      // `inline` y no el inline-block por defecto del botón: dentro
                      // de la frase tiene que fluir como texto, no centrarse.
                      className="inline cursor-pointer text-left font-semibold text-slate-800 hover:underline dark:text-slate-100"
                    >
                      {shortLabel(text.target)}
                    </button>
                    {text.tail}
                  </p>
                  <p className="mt-0.5 text-[11px] text-slate-400">
                    {shortDate(entry.at)}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {footnote && (
        <p className="mt-auto border-t border-dashed pt-3 text-xs text-slate-400">
          {footnote}
        </p>
      )}
    </section>
  );
}
